import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DrySpellMapService } from '../dry-spell-map/dry-spell-map.service';
import { DrySpellDataService } from '../dry-spell-data.service';
import { MONSOON_BLOCKS, SEASON_WINDOWS, YEAR_RANGE } from '../dry-spell-map/dry-spell-map-constants';
import { UtilsService } from 'src/app/utils/utils.service';

@Component({
  selector: 'app-dry-spell-sidebar',
  templateUrl: './dry-spell-sidebar.component.html',
  styleUrls: ['./dry-spell-sidebar.component.scss']
})
export class DrySpellSidebarComponent implements OnInit, OnDestroy {

  readonly SEASON_WINDOWS = SEASON_WINDOWS;
  readonly years: number[] = Array.from({ length: YEAR_RANGE.max - YEAR_RANGE.min + 1 }, (_, i) => YEAR_RANGE.min + i);

  mode: string = 'season';
  thresh: number = 14;
  selectedYear: number = YEAR_RANGE.max;

  compareYears: number[] = [YEAR_RANGE.max, YEAR_RANGE.max - 2, YEAR_RANGE.max - 5, YEAR_RANGE.min];
  compareImages: { [panelIdx: number]: string } = {};
  compareLoading: boolean = false;
  activeDropdown: number = -1;

  isPlaying: boolean = false;
  private playTimer: any;

  heatmapImages: { [key: string]: string } = {};
  loadingHeatmaps: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    public mapSrv: DrySpellMapService,
    private dataSrv: DrySpellDataService,
    private utils: UtilsService
  ) {}

  ngOnInit() {
    this.mapSrv.heatmapImages$.pipe(takeUntil(this.destroy$)).subscribe(images => {
      this.heatmapImages = images;
    });
    this.mapSrv.loadingHeatmaps$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.loadingHeatmaps = loading;
    });
    this.thresh = this.mapSrv.selectedThresh;
    this.selectedYear = this.mapSrv.selectedYear;
  }

  setMode(m: string) {
    if (this.mode === m) { return; }
    this.mode = m;
    this.mapSrv.selectedMode = m;
    if (m === 'year') {
      this.loadCompareImages();
    }
  }

  setThresh(t: number) {
    if (this.thresh === t) { return; }
    this.thresh = t;
    this.mapSrv.onThresholdChange(t);
    if (this.mode === 'year') {
      this.loadCompareImages();
    }
  }

  async loadCompareImages() {
    this.compareLoading = true;
    this.compareImages = {};
    const fullSeasonBlock = MONSOON_BLOCKS[this.thresh]['jun_jul_aug_sep'];
    const loads = this.compareYears.map((year, idx) =>
      this.dataSrv.getDrySpellHeatmap(year, fullSeasonBlock, 'IMD_GRID').toPromise()
        .then((res: any) => {
          if (res && res.base64Image) {
            this.compareImages[idx] = res.base64Image;
          }
        })
        .catch(() => {})
    );
    await Promise.all(loads);
    this.compareLoading = false;
  }

  async loadComparePanelImage(panelIdx: number) {
    const fullSeasonBlock = MONSOON_BLOCKS[this.thresh]['jun_jul_aug_sep'];
    try {
      const res: any = await this.dataSrv.getDrySpellHeatmap(
        this.compareYears[panelIdx], fullSeasonBlock, 'IMD_GRID'
      ).toPromise();
      if (res && res.base64Image) {
        this.compareImages = { ...this.compareImages, [panelIdx]: res.base64Image };
      }
    } catch (e) {}
  }

  onSliderChange(event: any) {
    const year = +event.target.value;
    if (this.selectedYear === year) { return; }
    this.selectedYear = year;
    this.mapSrv.onYearChange(this.selectedYear);
  }

  togglePlay() {
    this.isPlaying = !this.isPlaying;
    if (this.isPlaying) {
      this.playTimer = setInterval(() => {
        this.selectedYear = this.selectedYear >= YEAR_RANGE.max ? YEAR_RANGE.min : this.selectedYear + 1;
        this.mapSrv.onYearChange(this.selectedYear);
      }, 900);
    } else {
      clearInterval(this.playTimer);
    }
  }

  toggleDropdown(idx: number) {
    this.activeDropdown = this.activeDropdown === idx ? -1 : idx;
  }

  pickCompareYear(panelIdx: number, year: number) {
    if (this.compareYears[panelIdx] === year) {
      this.activeDropdown = -1;
      return;
    }
    this.compareYears = [...this.compareYears];
    this.compareYears[panelIdx] = year;
    this.activeDropdown = -1;
    this.loadComparePanelImage(panelIdx);
  }

  getImageForWindow(winKey: string): string {
    return this.heatmapImages[winKey] || '';
  }

  getCompareImage(panelIdx: number): string {
    return this.compareImages[panelIdx] || '';
  }

  onDocClick() {
    this.activeDropdown = -1;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.playTimer);
  }
}
